<script>
  import { onMount } from "svelte";
  import { DataTable, Pagination } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import SearchInput from "../../settings/components/SearchInput.svelte";

  let users = [];
  let loading = true;
  let error = "";
  let page = 1;
  let pageSize = 10;
  let searchUser = "";
  const historyHeaders = [
    { key: "username", value: "User" },
    { key: "email", value: "E-Mail" },
    { key: "last_active", value: "Last active" },
    { key: "total_days", value: "Total days" },
    { key: "generated_reports", value: "Generated reports" },
    { key: "generated_models", value: "Generated models" },
    { key: "bot_interactions", value: "Bot interactions" },
    { key: "actions", value: "Actions" },
  ];

  $: filteredUsers = searchUser
    ? users.filter((user) => user.username?.toLowerCase().includes(searchUser))
    : users;
  $: visibleUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);
  $: activeUsers = users.filter((user) => user.last_active && Date.now() - new Date(user.last_active).getTime() <= 30 * 86400000).length;
  $: reports = users.reduce((sum, user) => sum + (user.generated_reports || 0), 0);
  $: models = users.reduce((sum, user) => sum + (user.generated_models || 0), 0);

  onMount(async () => {
    try {
      const [historyResponse, usersResponse] = await Promise.all([
        fetchWithSession("/api/damprojects/admin/user-history"),
        fetchWithSession("/api/authentication/settings/users"),
      ]);
      if (!historyResponse.ok || !usersResponse.ok) throw new Error("User history could not be loaded.");
      const history = await historyResponse.json();
      const historyByName = new Map(history.map((user) => [user.username, user]));
      const existingUsers = (await usersResponse.json()).data || [];
      users = existingUsers.map((user) => ({
        ...user,
        username: user.username,
        ...historyByName.get(user.username),
      }));
    } catch (exception) {
      error = exception.message;
    } finally {
      loading = false;
    }
  });

  function formatDate(value) {
    if (!value) return "–";
    return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
  }

  function onSearch(event) {
    searchUser = (event.detail || "").toLowerCase();
    page = 1;
  }
</script>

<main>
  <div class="summary">
    <div class="kpi-box"><strong>{users.length}</strong><span>Users</span></div>
    <div class="kpi-box"><strong>{activeUsers}</strong><span>Active</span></div>
    <div class="kpi-box"><strong>{reports}</strong><span>Reports</span></div>
    <div class="kpi-box"><strong>{models}</strong><span>Models</span></div>
    <div class="search-toolbar"><SearchInput placeholder="Search user history..." on:search={onSearch} /></div>
  </div>

  <section>
    {#if loading}
      <p>Loading user history...</p>
    {:else if error}
      <p class="error">{error}</p>
    {:else if !users.length}
      <p>No user activity has been recorded yet.</p>
    {:else}
      <div class="table-wrapper">
        <DataTable headers={historyHeaders} rows={visibleUsers} class="custom-table">
          <svelte:fragment slot="cell" let:row let:cell>
            {#if cell.key === "last_active"}
              {formatDate(row.last_active)}
            {:else if cell.key === "total_days" || cell.key === "generated_reports" || cell.key === "generated_models" || cell.key === "bot_interactions"}
              {row[cell.key] || 0}
            {:else if cell.key === "actions"}
              <button class="history-action" disabled>View logs (0)</button>
            {:else}
              {cell?.value || "-"}
            {/if}
          </svelte:fragment>
        </DataTable>
      </div>
      <Pagination bind:page bind:pageSize totalItems={filteredUsers.length} pageSizes={[10, 25, 50]} />
    {/if}
  </section>
</main>

<style>
  main { width: 100%; }
  .summary { display: flex; align-items: center; gap: var(--control-gap); margin-bottom: var(--control-gap); }
  .kpi-box { display: flex; align-items: center; justify-content: center; gap: 0.25rem; min-width: 7rem; height: 3rem; box-sizing: border-box; padding: 0.75rem 1rem; border: 1px solid var(--pax-black); border-radius: 5px; white-space: nowrap; }
  .kpi-box strong, .kpi-box span { color: var(--pax-black); }
  .search-toolbar { display: flex; flex: 1; min-width: 0; }
  .search-toolbar :global(.search-input) { flex: 1; min-width: 0; border-color: var(--pax-black); }
  section { background: var(--pax-white); overflow: hidden; }
  .table-wrapper { overflow-x: auto; }
  .history-action { padding: 10px 14px; border: 0; border-radius: 5px; background: var(--pax-black); color: var(--pax-white); }
  .history-action:disabled { opacity: 1; }
  :global(.custom-table th:last-child), :global(.custom-table td:last-child) { width: 1%; white-space: nowrap; text-align: left; }
  .error { color: var(--pax-red); }
</style>
